import React, { useState } from 'react';
import {
  Box,
  Drawer,
  AppBar,
  Toolbar,
  List,
  Typography,
  Divider,
  IconButton,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Avatar,
  Menu,
  MenuItem,
  Badge,
  useTheme,
  useMediaQuery,
  BottomNavigation,
  BottomNavigationAction,
  Paper,
  TextField,
  Link
} from '@mui/material';
import {
  Menu as MenuIcon,
  Home,
  Handshake,
  Store,
  People,
  Chat,
  LocationOn,
  Person,
  Settings,
  Logout,
  Notifications,
  Security,
  Feedback,
  Forum,
  AutoStories,
  Search as SearchIcon
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { useSocket } from '../../hooks/useSocket';
import FeedbackDialog from '../UI/FeedbackDialog';
import { useTranslation } from '../../context/LanguageContext';
import NebenanHilfeLogo from '../UI/NebenanHilfeLogo';

const drawerWidth = 240;

const DashboardLayout = ({ children, currentSection, onSectionChange }) => {
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const { user, logout } = useAuth();
  const { unreadCount } = useSocket();
  const { t } = useTranslation();
  
  const [mobileOpen, setMobileOpen] = useState(false);
  const [anchorEl, setAnchorEl] = useState(null);
  const [notifAnchorEl, setNotifAnchorEl] = useState(null);
  const [feedbackOpen, setFeedbackOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');

  const handleSearchSubmit = (e) => {
    e.preventDefault();
    if (searchQuery.trim()) {
      navigate(`/dashboard/search?q=${encodeURIComponent(searchQuery.trim())}`);
      setSearchQuery('');
    }
  };

  const menuItems = [
    ...(isMobile ? [{ id: 'hub', label: 'Übersicht (Hub)', icon: <Home /> }] : []),
    { id: 'miteinander', label: 'Miteinander', icon: <Handshake /> },
    { id: 'marktplatz', label: 'Marktplatz', icon: <Store /> },
    { id: 'freunde', label: 'Freunde', icon: <People /> },
    { id: 'chat', label: 'Chat', icon: <Chat /> },
    { id: 'forum', label: 'Forum', icon: <Forum /> },
    { id: 'wissen', label: 'Wissen & Blog', icon: <AutoStories /> },
    { id: 'location', label: 'Standort', icon: <LocationOn /> },
    { id: 'profile', label: 'Profil', icon: <Person /> },
    { id: 'settings', label: 'Einstellungen', icon: <Settings /> },
    { id: 'security', label: 'Sicherheit', icon: <Security /> }
  ];

  const handleDrawerToggle = () => {
    setMobileOpen(!mobileOpen);
  };

  const handleMenuClick = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  const handleLogout = () => {
    handleMenuClose();
    logout();
  };

  const drawer = (
    <div>
      <Toolbar sx={{ px: 2, minHeight: 70 }}>
        <Box 
          onClick={() => onSectionChange(isMobile ? 'hub' : 'miteinander')}
          sx={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-start', width: '100%', cursor: 'pointer' }}
        >
          <NebenanHilfeLogo size="medium" lightText={theme?.palette?.mode === 'dark'} />
        </Box>
      </Toolbar>
      <Divider />
      <List sx={{ px: 1, py: 1 }}>
        {menuItems.map((item) => (
          <ListItem key={item.id} disablePadding sx={{ mb: 0.5 }}>
            <ListItemButton
              selected={currentSection === item.id}
              onClick={() => {
                onSectionChange(item.id);
                if (isMobile) setMobileOpen(false);
              }}
              sx={{
                borderRadius: '10px',
                py: 1,
                px: 1.5,
                transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
                '&.Mui-selected': {
                  backgroundColor: theme?.palette?.mode === 'dark' ? 'rgba(59, 130, 246, 0.16)' : 'rgba(15, 23, 42, 0.08)',
                  color: 'primary.main',
                  '&:hover': {
                    backgroundColor: theme?.palette?.mode === 'dark' ? 'rgba(59, 130, 246, 0.24)' : 'rgba(15, 23, 42, 0.12)',
                  }
                },
                '&:hover': {
                  transform: 'translateX(3px)',
                }
              }}
            >
              <ListItemIcon sx={{ minWidth: 38, color: currentSection === item.id ? 'primary.main' : 'text.secondary' }}>
                {item.id === 'chat' ? (
                  <Badge badgeContent={unreadCount} color="error">
                    {item.icon}
                  </Badge>
                ) : (
                  item.icon
                )}
              </ListItemIcon>
              <ListItemText 
                primary={t(item.id)} 
                primaryTypographyProps={{ sx: { textTransform: 'capitalize', fontWeight: currentSection === item.id ? 700 : 500, fontSize: '0.9rem' } }} 
              />
            </ListItemButton>
          </ListItem>
        ))}
      </List>
      <Divider sx={{ mx: 2, my: 1 }} />
      <List sx={{ px: 1 }}>
        <ListItem disablePadding>
          <ListItemButton 
            onClick={() => setFeedbackOpen(true)}
            sx={{ borderRadius: '10px', py: 1, px: 1.5 }}
          >
            <ListItemIcon sx={{ minWidth: 38, color: 'text.secondary' }}>
              <Feedback />
            </ListItemIcon>
            <ListItemText primary="Feedback" primaryTypographyProps={{ sx: { fontWeight: 500, fontSize: '0.9rem' } }} />
          </ListItemButton>
        </ListItem>
      </List>

      <Box sx={{ p: 2, mt: 2, textAlign: 'center', bgcolor: 'action.hover', mx: 1.5, mb: 2, borderRadius: 2 }}>
        <Typography variant="caption" sx={{ color: 'success.main', fontWeight: 700, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 0.5, mb: 0.5 }}>
          🇩🇪 Server in Deutschland
        </Typography>
        <Typography variant="caption" sx={{ color: 'text.secondary', display: 'block', fontSize: '0.72rem', mb: 1 }}>
          100% DSGVO & ISO 27001
        </Typography>
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: 1 }}>
          <Link href="/agb" target="_blank" rel="noopener noreferrer" underline="hover" sx={{ fontSize: '0.75rem', color: 'text.secondary', fontWeight: 600 }}>
            AGB
          </Link>
          <Typography variant="caption" sx={{ color: 'text.disabled' }}>•</Typography>
          <Link href="/datenschutz" target="_blank" rel="noopener noreferrer" underline="hover" sx={{ fontSize: '0.75rem', color: 'text.secondary', fontWeight: 600 }}>
            Datenschutz
          </Link>
        </Box>
      </Box>
    </div>
  );

  return (
    <Box sx={{ display: 'flex' }}>
      <AppBar
        position="fixed"
        sx={{
          width: { md: `calc(100% - ${drawerWidth}px)` },
          ml: { md: `${drawerWidth}px` },
        }}
      >
        <Toolbar>
          <IconButton
            color="inherit"
            aria-label="open drawer"
            edge="start"
            onClick={handleDrawerToggle}
            sx={{ mr: 2, display: { md: 'none' } }}
          >
            <MenuIcon />
          </IconButton>
          
          {/* Current page title */}
          <Typography 
            variant="subtitle1" 
            noWrap 
            component="div" 
            sx={{ 
              display: { xs: 'none', sm: 'block' }, 
              mr: 2, 
              fontWeight: 600,
              color: 'text.primary',
              letterSpacing: '-0.2px'
            }}
          >
            {t(currentSection) || 'Dashboard'}
          </Typography>

          {/* Search Bar */}
          <Box 
            component="form" 
            onSubmit={handleSearchSubmit}
            sx={{ 
              position: 'relative', 
              borderRadius: '24px', 
              border: '1px solid',
              borderColor: 'divider',
              backgroundColor: theme?.palette?.mode === 'dark' ? 'rgba(255, 255, 255, 0.05)' : 'rgba(15, 23, 42, 0.04)',
              '&:hover': { borderColor: 'primary.light' },
              '&:focus-within': {
                borderColor: 'primary.main',
                boxShadow: '0 0 0 3px rgba(37, 99, 235, 0.12)',
                backgroundColor: 'background.paper',
              },
              mr: 2,
              ml: 0,
              width: '100%',
              maxWidth: { xs: '100%', sm: '320px' },
              display: 'flex',
              alignItems: 'center',
              transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)'
            }}
          >
            <Box sx={{ p: '0 14px', height: '100%', position: 'absolute', pointerEvents: 'none', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <SearchIcon color="action" fontSize="small" />
            </Box>
            <TextField
              size="small"
              placeholder="Nachbarschaft durchsuchen..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              variant="standard"
              InputProps={{
                disableUnderline: true,
                sx: { 
                  pl: '42px', 
                  pr: '14px', 
                  py: '6px',
                  width: '100%',
                  fontSize: '0.875rem'
                }
              }}
              sx={{ width: '100%' }}
            />
          </Box>
          
          <Box sx={{ flexGrow: 1 }} />

          <IconButton 
            color="inherit" 
            sx={{ mr: 1 }}
            onClick={(e) => setNotifAnchorEl(e.currentTarget)}
          >
            <Badge badgeContent={unreadCount > 0 ? unreadCount : 0} color="error">
              <Notifications />
            </Badge>
          </IconButton>

          {/* Benachrichtigungs-Menü */}
          <Menu
            anchorEl={notifAnchorEl}
            open={Boolean(notifAnchorEl)}
            onClose={() => setNotifAnchorEl(null)}
            PaperProps={{
              sx: { width: 320, maxWidth: '90vw', maxHeight: 400, mt: 1, borderRadius: 2 }
            }}
          >
            <Box sx={{ px: 2, py: 1.5, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <Typography variant="subtitle1" fontWeight="bold">Benachrichtigungen</Typography>
              <Typography variant="caption" color="primary" sx={{ cursor: 'pointer', fontWeight: 600 }}>
                Alle gelesen
              </Typography>
            </Box>
            <Divider />
            <MenuItem onClick={() => { setNotifAnchorEl(null); onSectionChange('miteinander'); }} sx={{ py: 1.5 }}>
              <Box>
                <Typography variant="body2" fontWeight={600}>Willkommen bei NebenanHilfe!</Typography>
                <Typography variant="caption" color="text.secondary">Dein Quartier vernetzt sich – schön, dass du dabei bist.</Typography>
              </Box>
            </MenuItem>
            <MenuItem onClick={() => { setNotifAnchorEl(null); onSectionChange('chat'); }} sx={{ py: 1.5 }}>
              <Box>
                <Typography variant="body2" fontWeight={600}>Gemeinsam helfen</Typography>
                <Typography variant="caption" color="text.secondary">Schau in den Chat oder erstelle ein Gesuch.</Typography>
              </Box>
            </MenuItem>
          </Menu>

          <IconButton
            onClick={handleMenuClick}
            sx={{ p: 0 }}
          >
            <Avatar 
              src={user?.avatar && (user.avatar.startsWith('data:') || user.avatar.startsWith('http')) ? user.avatar : undefined}
              sx={{ bgcolor: 'secondary.main', color: 'secondary.contrastText', fontSize: '0.875rem', fontWeight: 600 }}
            >
              {user?.avatar && !user.avatar.startsWith('data:') && !user.avatar.startsWith('http') ? (
                user.avatar
              ) : user?.firstName && user?.lastName ? (
                `${user.firstName[0]}${user.lastName[0]}`.toUpperCase()
              ) : (
                <Person fontSize="small" />
              )}
            </Avatar>
          </IconButton>

          <Menu
            anchorEl={anchorEl}
            open={Boolean(anchorEl)}
            onClose={handleMenuClose}
            onClick={handleMenuClose}
          >
            <MenuItem disabled>
              <Typography variant="subtitle2">
                {user?.firstName} {user?.lastName}
              </Typography>
            </MenuItem>
            <Divider />
            <MenuItem onClick={() => onSectionChange('profile')}>
              <Person sx={{ mr: 1 }} />
              Profil
            </MenuItem>
            <MenuItem onClick={() => onSectionChange('settings')}>
              <Settings sx={{ mr: 1 }} />
              Einstellungen
            </MenuItem>
            <Divider />
            <MenuItem onClick={handleLogout}>
              <Logout sx={{ mr: 1 }} />
              Abmelden
            </MenuItem>
          </Menu>
        </Toolbar>
      </AppBar>

      <Box
        component="nav"
        sx={{ width: { md: drawerWidth }, flexShrink: { md: 0 } }}
      >
        <Drawer
          variant="temporary"
          open={mobileOpen}
          onClose={handleDrawerToggle}
          ModalProps={{
            keepMounted: true,
          }}
          sx={{
            display: { xs: 'block', md: 'none' },
            '& .MuiDrawer-paper': { boxSizing: 'border-box', width: drawerWidth },
          }}
        >
          {drawer}
        </Drawer>
        <Drawer
          variant="permanent"
          sx={{
            display: { xs: 'none', md: 'block' },
            '& .MuiDrawer-paper': { boxSizing: 'border-box', width: drawerWidth },
          }}
          open
        >
          {drawer}
        </Drawer>
      </Box>

      <Box
        component="main"
        sx={{
          flexGrow: 1,
          p: { xs: 1.5, sm: 2.5, md: 3 },
          width: { md: `calc(100% - ${drawerWidth}px)` },
          mt: { xs: 7, sm: 8 },
          mb: isMobile ? 8 : 0 // Safe padding for mobile bottom nav
        }}
      >
        {children}
      </Box>


      {isMobile && (
        <Paper 
          sx={{ 
            position: 'fixed', 
            bottom: 0, 
            left: 0, 
            right: 0, 
            zIndex: 1100,
            pb: 'env(safe-area-inset-bottom, 0px)',
            backdropFilter: 'blur(16px)',
            WebkitBackdropFilter: 'blur(16px)',
            backgroundColor: theme?.palette?.mode === 'dark' ? 'rgba(17, 24, 39, 0.92)' : 'rgba(255, 255, 255, 0.92)',
            borderTop: '1px solid',
            borderColor: 'divider',
            boxShadow: '0 -4px 20px rgba(0, 0, 0, 0.08)'
          }} 
          elevation={4}
        >
          <BottomNavigation
            showLabels
            value={currentSection}
            onChange={(event, newValue) => {
              onSectionChange(newValue);
            }}
            sx={{
              height: 64,
              backgroundColor: 'transparent',
              '& .MuiBottomNavigationAction-root': {
                minWidth: 'auto',
                py: 0.8,
                px: { xs: 0.5, sm: 1 },
                transition: 'all 0.2s ease',
                '&.Mui-selected': {
                  color: 'primary.main',
                  transform: 'translateY(-2px)'
                },
                '& .MuiBottomNavigationAction-label': {
                  fontSize: '0.68rem',
                  fontWeight: 500,
                  mt: 0.2,
                  '&.Mui-selected': {
                    fontSize: '0.72rem',
                    fontWeight: 700
                  }
                }
              }
            }}
          >
            <BottomNavigationAction label="Start" value="hub" icon={<Home />} />
            <BottomNavigationAction label="Miteinander" value="miteinander" icon={<Handshake />} />
            <BottomNavigationAction label="Marktplatz" value="marktplatz" icon={<Store />} />
            <BottomNavigationAction label="Freunde" value="freunde" icon={<People />} />
            <BottomNavigationAction 
              label="Nachrichten" 
              value="chat" 
              icon={
                <Badge badgeContent={unreadCount} color="error">
                  <Chat />
                </Badge>
              } 
            />
          </BottomNavigation>
        </Paper>
      )}

      <FeedbackDialog open={feedbackOpen} onClose={() => setFeedbackOpen(false)} />
    </Box>
  );
};

export default DashboardLayout;
